/**
 * Tile de un archivo adjunto de una nota de aseo (foto / video / documento).
 *
 * Pensado para el dedo (round 2026-10-26):
 *  - En <= 480 px la grilla es de 2 columnas y el tile ocupa el ancho de la
 *    columna (ver `Css/notas-movil.css`); en escritorio sigue siendo de 96 px.
 *  - Botón de borrar de 44 px en táctil, SIEMPRE con confirmación.
 *  - Videos: el tile muestra el poster (miniatura) con un botón de play; el
 *    <video> real solo existe en el visor a pantalla completa y con
 *    `preload="none"`, así abrir las notas no descarga datos de ningún video.
 *    Antes había un <video controls> de 96x96 por tile, inutilizable con el dedo.
 *  - Fotos: visor a pantalla completa del `Image` de AntD (preview).
 */

import { useState } from "react";
import { Button, Image, Modal, Popconfirm, Tag, Tooltip, Typography } from "antd";
import {
  DeleteOutlined,
  FileExcelOutlined,
  FileOutlined,
  FilePdfOutlined,
  FilePptOutlined,
  FileTextOutlined,
  FileWordOutlined,
  PlayCircleFilled,
} from "@ant-design/icons";
import VideoPlayer from "../../../Components/VideoPlayer";
import "../../../Css/notas-movil.css";

const { Text } = Typography;

export type KindArchivo = "image" | "video" | "doc";

/** Forma normalizada para renderizar un tile (sirve a ambos modos de la nota). */
export interface Tile {
  key: string;
  kind: KindArchivo;
  mimeType: string;
  name: string;
  /** Fuente para thumbnail/preview embebido (thumb o el propio archivo). */
  previewSrc: string;
  /** Fuente a tamaño completo / para abrir en pestaña nueva. */
  fullSrc: string;
  /**
   * Solo videos: URL firmada de la versión liviana (480p, `previewUrl` del entry de storage).
   * Con ella el visor abre en calidad baja y ofrece pasar a HD; sin ella reproduce `fullSrc`.
   */
  videoLiviano?: string | null;
}

export function kindOf(mime: string | undefined, kind?: string): KindArchivo {
  if (kind === "image" || kind === "video") return kind;
  const m = (mime ?? "").toLowerCase();
  if (m.startsWith("image/")) return "image";
  if (m.startsWith("video/")) return "video";
  return "doc";
}

/** Etiqueta corta del tipo de documento (para el Tag). */
export function docLabel(mime: string | undefined): string {
  const m = (mime ?? "").toLowerCase();
  if (m.includes("pdf")) return "PDF";
  if (m.includes("word") || m.includes("msword")) return "Word";
  if (m.includes("excel") || m.includes("spreadsheet")) return "Excel";
  if (m.includes("csv")) return "CSV";
  if (m.includes("powerpoint") || m.includes("presentation")) return "PPT";
  if (m.startsWith("text/")) return "Texto";
  return "Doc";
}

/** Icono por tipo de documento (mismo criterio visual que GuestFilesManager). */
export function DocIcon({ mime, size = 30 }: { mime?: string; size?: number }) {
  const m = (mime ?? "").toLowerCase();
  const style = { fontSize: size } as const;
  if (m.includes("pdf")) return <FilePdfOutlined style={{ ...style, color: "#f5222d" }} />;
  if (m.includes("word") || m.includes("msword"))
    return <FileWordOutlined style={{ ...style, color: "#1677ff" }} />;
  if (m.includes("excel") || m.includes("spreadsheet") || m.includes("csv"))
    return <FileExcelOutlined style={{ ...style, color: "#52c41a" }} />;
  if (m.includes("powerpoint") || m.includes("presentation"))
    return <FilePptOutlined style={{ ...style, color: "#fa8c16" }} />;
  if (m.startsWith("text/"))
    return <FileTextOutlined style={{ ...style, color: "#8c8c8c" }} />;
  return <FileOutlined style={{ ...style, color: "#8c8c8c" }} />;
}

/** Visor de video a pantalla completa (se monta solo al tocar el tile). */
function VisorVideo({
  open,
  src,
  liviano,
  poster,
  nombre,
  onClose,
}: {
  open: boolean;
  src: string;
  liviano?: string | null;
  poster?: string;
  nombre: string;
  onClose: () => void;
}) {
  return (
    <Modal
      open={open}
      onCancel={onClose}
      footer={null}
      destroyOnClose
      width="min(100vw, 1100px)"
      // Anclado ARRIBA (no centrado): el escenario del video reserva su tamaño de antemano y, si el modal
      // fuera centrado, cualquier ajuste de alto lo movería. Ver Components/VideoPlayer.tsx.
      style={{ top: 12 }}
      className="nm-visor"
      title={<span style={{ color: "#fff" }}>{nombre}</span>}
      styles={{
        header: { background: "#000", marginBottom: 0, padding: "10px 16px" },
        body: { padding: "0 8px 8px", background: "#000" },
      }}
      closeIcon={<span style={{ color: "#fff", fontSize: 22 }}>×</span>}
    >
      <VideoPlayer
        className="nm-visor-video"
        testId="nm-visor-video"
        src={src}
        previewSrc={liviano}
        poster={poster}
        nombreArchivo={nombre}
        altoMaximo="calc(100dvh - 150px)"
        autoPlay
        playsInline
        preload="none"
      />
    </Modal>
  );
}

export function NotaArchivoTile({
  tile,
  editable,
  onDelete,
}: {
  tile: Tile;
  editable: boolean;
  onDelete: () => void;
}) {
  const [verVideo, setVerVideo] = useState(false);
  const poster = tile.previewSrc && tile.previewSrc !== tile.fullSrc ? tile.previewSrc : undefined;

  return (
    <div className="nm-tile" data-testid="nm-tile" data-kind={tile.kind}>
      {tile.kind === "image" ? (
        <div className="nm-tile-img">
          <Image
            src={tile.previewSrc}
            preview={{ src: tile.fullSrc }}
            alt={tile.name}
            style={{ objectFit: "cover", borderRadius: 6 }}
          />
        </div>
      ) : tile.kind === "video" ? (
        <>
          <button
            type="button"
            className="nm-tile-media"
            data-testid="nm-tile-video"
            aria-label={`Reproducir ${tile.name}`}
            onClick={() => setVerVideo(true)}
          >
            {poster ? <img className="nm-poster" src={poster} alt="" loading="lazy" /> : null}
            <span className="nm-tile-play">
              <PlayCircleFilled />
            </span>
          </button>
          {verVideo && (
            <VisorVideo
              open
              src={tile.fullSrc}
              liviano={tile.videoLiviano}
              poster={poster}
              nombre={tile.name}
              onClose={() => setVerVideo(false)}
            />
          )}
        </>
      ) : (
        <Tooltip title={`Abrir ${tile.name}`}>
          <div
            className="nm-tile-doc"
            data-testid="nm-tile-doc"
            role="button"
            tabIndex={0}
            onClick={() => window.open(tile.fullSrc, "_blank", "noopener,noreferrer")}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") {
                window.open(tile.fullSrc, "_blank", "noopener,noreferrer");
              }
            }}
          >
            <DocIcon mime={tile.mimeType} />
            <Tag color="default" style={{ margin: 0, fontSize: 12, lineHeight: "18px" }}>
              {docLabel(tile.mimeType)}
            </Tag>
            <Text className="nm-tile-nombre" ellipsis={{ tooltip: tile.name }}>
              {tile.name}
            </Text>
          </div>
        </Tooltip>
      )}
      {editable && (
        <Popconfirm
          title="¿Borrar este archivo?"
          okText="Borrar"
          cancelText="Cancelar"
          onConfirm={onDelete}
        >
          <Button
            danger
            size="small"
            className="nm-borrar"
            data-testid="nm-borrar"
            aria-label={`Borrar ${tile.name}`}
            icon={<DeleteOutlined />}
          />
        </Popconfirm>
      )}
    </div>
  );
}
